"use client";

/**
 * Search by photo, step one (owner, 2026-10-20): the camera button opens
 * this instead of the file picker. Desktop gets a centred modal with a drop
 * zone, phones a bottom sheet (the app's pattern: ConfirmDialog). A photo
 * can be dropped, chosen, taken with the phone's camera, or pasted (a
 * copied picture, or a link to one), and the dialog says so, since pasting
 * is not obvious. One photo at a time, only the formats the model reads
 * (lib/photo-pick.ts).
 *
 * The scene changes only while a file is dragged over the dialog, not on a
 * plain hover (owner, 2026-10-20). A file it cannot read turns the zone
 * rose while it is dragged and leaves the format note behind, and the
 * whole dialog takes the drop, so a near miss never opens the file in the
 * tab (owner, 2026-10-21). The zone has no fill of its own: the dark-mode
 * gray swap made it a black hole in the card.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, ImageIcon, TriangleAlert, X } from "lucide-react";
import BottomSheet from "@/components/BottomSheet";
import Illo from "@/components/Illo";
import { useT } from "@/lib/locale-context";
import { looksLikeImageLink } from "@/lib/listing-url";
import { useIsMobileViewport } from "@/lib/use-install-prompt";
export { IMAGE_ACCEPT, pickPhoto, type PhotoNote } from "@/lib/photo-pick";
import { IMAGE_ACCEPT, dragVerdict, pickPhoto, type DragVerdict, type PhotoNote } from "@/lib/photo-pick";

type ZoneProps = {
  onFile: (file: File) => void;
  onLink: (url: string) => void;
  /** More than one photo (the first is taken) or none it can read. */
  onNote: (note: PhotoNote | null) => void;
  note: PhotoNote | null;
};

/** The two scenes share a frame: the photo hovering above it, and the
 *  photo settled in it with every window lit while a file is over the
 *  dialog. A file it cannot read fades the resting scene. */
function Scene({ active, dim = false, className }: { active: boolean; dim?: boolean; className: string }) {
  return (
    <span className={`relative block transition-opacity duration-200 ${dim ? "opacity-40" : ""} ${className}`} aria-hidden="true">
      <Illo
        base="/illustrations/imagesearch/drop_idle"
        className={`absolute inset-0 h-full w-full scale-[1.3] object-contain transition-opacity duration-200 ${active ? "opacity-0" : ""}`}
      />
      <Illo
        base="/illustrations/imagesearch/drop_active"
        className={`absolute inset-0 h-full w-full scale-[1.3] object-contain transition-opacity duration-200 ${active ? "" : "opacity-0"}`}
      />
    </span>
  );
}

function NoteLine({ note }: { note: PhotoNote | null }) {
  const t = useT();
  if (!note) return null;
  if (note === "one_at_a_time") {
    return <p className="mt-3 text-sm font-medium text-amber-700" role="status">{t("listings.magic.drop.one_at_a_time")}</p>;
  }
  return (
    <p className="mt-3 flex items-start justify-center gap-1.5 text-sm font-medium text-rose-700" role="alert">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
      {t("listings.magic.drop.format")}
    </p>
  );
}

/** Takes the files of a drop, a paste or a pick: one readable photo goes
 *  to onFile, anything else becomes a note. */
function useTake({ onFile, onNote }: Pick<ZoneProps, "onFile" | "onNote">) {
  return (files: ArrayLike<File> | null | undefined): boolean => {
    const { file, extra, unsupported } = pickPhoto(files);
    if (file) {
      onNote(extra ? "one_at_a_time" : null);
      onFile(file);
      return true;
    }
    if (unsupported) {
      onNote("format");
      return true;
    }
    return false;
  };
}

/** A paste anywhere while the dialog is open. The search box's own paste
 *  handler runs first and marks what it took (defaultPrevented). */
function usePaste(take: (files: ArrayLike<File> | null | undefined) => boolean, onLink: (url: string) => void) {
  const latest = useRef({ take, onLink });
  latest.current = { take, onLink };
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented) return;
      if (latest.current.take(e.clipboardData?.files)) {
        e.preventDefault();
        return;
      }
      const text = (e.clipboardData?.getData("text") ?? "").trim();
      if (text && looksLikeImageLink(text)) {
        e.preventDefault();
        latest.current.onLink(text);
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);
}

/** Desktop: drop, choose, or paste with this machine's keys. `drag` is
 *  what is being dragged over the dialog right now. */
function DesktopZone({ drag, onFile, onLink, onNote, note }: ZoneProps & { drag: DragVerdict | null }) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  // The paste keys of this machine; Ctrl+V until mounted (server render).
  const [keys, setKeys] = useState("Ctrl+V");
  useEffect(() => {
    if (/Mac|iPhone|iPad/i.test(navigator.userAgent)) setKeys("⌘V");
  }, []);
  const take = useTake({ onFile, onNote });
  usePaste(take, onLink);
  const choose = () => inputRef.current?.click();
  const bad = drag === "bad";
  const over = drag === "ok" || drag === "unknown";

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="hidden"
        onChange={(e) => {
          take(e.target.files);
          e.target.value = "";
        }}
      />
      <div
        role="button"
        tabIndex={0}
        aria-label={t("listings.magic.drop.choose")}
        aria-describedby="image-drop-help"
        data-drag={drag ?? undefined}
        onClick={choose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            choose();
          }
        }}
        className={`flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed px-6 pb-6 pt-4 text-center outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${
          bad ? "border-rose-500" : over ? "border-indigo-500" : "border-gray-300 hover:border-gray-400 focus-visible:border-indigo-500"
        }`}
      >
        <Scene active={over} dim={bad} className="h-44 w-72" />
        <span className={`mt-2 text-base font-semibold ${bad ? "text-rose-700" : "text-gray-900"}`}>
          {t(bad ? "listings.magic.drop.bad_title" : "listings.magic.drop.title")}
        </span>
        <span className="mt-1 max-w-sm text-sm text-gray-500">
          {t(bad ? "listings.magic.drop.bad_sub" : "listings.magic.drop.sub")}
        </span>
        <span className="mt-4 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700">
          {t("listings.magic.drop.choose")}
        </span>
        <span id="image-drop-help" className="mt-4 max-w-sm text-xs text-gray-500">
          {t("listings.magic.drop.paste").replace("{keys}", keys)}
          <span className="mt-1 block text-gray-400">{t("listings.magic.drop.formats")}</span>
        </span>
      </div>
      <NoteLine note={drag ? null : note} />
    </>
  );
}

/** Phones: nothing is dragged there; take a photo (the building in front
 *  of you) or pick one from the gallery. */
function PhoneZone({ onFile, onLink, onNote, note }: ZoneProps) {
  const t = useT();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const take = useTake({ onFile, onNote });
  usePaste(take, onLink);
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    take(e.target.files);
    e.target.value = "";
  };
  return (
    <div className="flex flex-col items-center pb-2 text-center">
      <input ref={cameraRef} type="file" accept={IMAGE_ACCEPT} capture="environment" className="hidden" onChange={onChange} />
      <input ref={galleryRef} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={onChange} />
      <Scene active={false} className="h-36 w-56" />
      <p className="mt-1 text-base font-semibold text-gray-900">{t("listings.magic.drop.title_touch")}</p>
      <p className="mt-1 max-w-xs text-sm text-gray-500">{t("listings.magic.drop.sub")}</p>
      <div className="mt-4 grid w-full grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3 py-3 text-sm font-semibold text-white active:bg-indigo-700"
        >
          <Camera className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          {t("listings.magic.drop.camera")}
        </button>
        <button
          type="button"
          onClick={() => galleryRef.current?.click()}
          className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-800 active:bg-gray-50"
        >
          <ImageIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          {t("listings.magic.drop.gallery")}
        </button>
      </div>
      <p className="mt-4 max-w-xs text-xs text-gray-500">
        {t("listings.magic.drop.paste_touch")}
        <span className="mt-1 block text-gray-400">{t("listings.magic.drop.formats")}</span>
      </p>
      <NoteLine note={note} />
    </div>
  );
}

/** The first link a drop carries (text/uri-list allows # comments). */
function droppedLink(dt: DataTransfer | null): string | null {
  const raw = dt?.getData("text/uri-list") || dt?.getData("text/plain") || "";
  return raw.split(/\r?\n/).map((l) => l.trim()).find((l) => l && !l.startsWith("#")) ?? null;
}

/** The dialog around the zone: a centred card on desktop, the shared
 *  bottom sheet on phones. */
export default function ImageSearchDialog({ open, onClose, ...zone }: ZoneProps & { open: boolean; onClose: () => void }) {
  const t = useT();
  const mobile = useIsMobileViewport();
  const take = useTake(zone);
  // What is dragged over the dialog; a depth count because dragenter and
  // dragleave fire for every child the pointer crosses.
  const [drag, setDrag] = useState<DragVerdict | null>(null);
  const depth = useRef(0);
  const sawBad = useRef(false);

  useEffect(() => {
    if (!open || mobile) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, mobile, onClose]);
  useEffect(() => {
    if (open) return;
    depth.current = 0;
    sawBad.current = false;
    setDrag(null);
  }, [open]);

  if (mobile) {
    return (
      <BottomSheet open={open} onClose={onClose} title={t("listings.magic.image.button")}>
        {open && <PhoneZone {...zone} />}
      </BottomSheet>
    );
  }
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-search-title"
      onClick={onClose}
      onDragEnter={(e) => {
        const v = dragVerdict(e.dataTransfer);
        if (!v) return;
        e.preventDefault();
        depth.current += 1;
        if (v === "bad") sawBad.current = true;
        setDrag(v);
      }}
      onDragOver={(e) => {
        const v = dragVerdict(e.dataTransfer);
        if (!v) return;
        e.preventDefault();
        // A file it cannot read shows the "no" cursor; the drag still
        // ends in the format note (below), never in silence.
        e.dataTransfer.dropEffect = v === "bad" ? "none" : "copy";
      }}
      onDragLeave={() => {
        if (depth.current === 0) return;
        depth.current -= 1;
        if (depth.current > 0) return;
        setDrag(null);
        if (sawBad.current) zone.onNote("format");
        sawBad.current = false;
      }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        sawBad.current = false;
        setDrag(null);
        if (take(e.dataTransfer?.files)) return;
        const link = droppedLink(e.dataTransfer);
        if (link && looksLikeImageLink(link)) {
          zone.onNote(null);
          zone.onLink(link);
        } else if (link) {
          zone.onNote("format");
        }
      }}
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 modal-backdrop-enter"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-5 shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)] modal-enter"
      >
        <div className="mb-3 flex items-center justify-between">
          <p id="image-search-title" className="text-base font-bold tracking-tight text-gray-900">
            {t("listings.magic.image.button")}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("listings.magic.drop.close")}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
        <DesktopZone drag={drag} {...zone} />
      </div>
    </div>,
    document.body,
  );
}
